import { useState, useEffect } from 'react';

export function useCountdown(targetISO, isLocked) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (isLocked || !targetISO) { setRemaining(null); return; }
    const update = () => {
      const diff = new Date(targetISO) - Date.now();
      if (diff <= 0) { setRemaining(null); return; }
      const totalSecs = Math.floor(diff / 1000);
      const d = Math.floor(totalSecs / 86400);
      const h = Math.floor((totalSecs % 86400) / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      setRemaining({ d, h, m, s, diff });
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [targetISO, isLocked]);

  return remaining;
}

// Compact badge for nav
export function CountdownBadge({ lockAt, isLocked }) {
  const remaining = useCountdown(lockAt, isLocked);
  if (!remaining) return null;
  // Only show within 48 hours
  if (remaining.diff > 48 * 3600000) return null;

  const urgent = remaining.diff < 3600000; // under 1 hour
  const label = remaining.d > 0
    ? remaining.d + 'd ' + remaining.h + 'h'
    : remaining.h > 0
    ? remaining.h + 'h ' + String(remaining.m).padStart(2,'0') + 'm'
    : String(remaining.m).padStart(2,'0') + ':' + String(remaining.s).padStart(2,'0');

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 14,
      background: urgent ? 'rgba(213,10,10,0.25)' : 'rgba(255,160,0,0.15)',
      border: '1px solid ' + (urgent ? 'rgba(213,10,10,0.4)' : 'rgba(255,160,0,0.3)'),
      color: urgent ? '#ff6060' : '#ffa000',
      fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
      animation: urgent ? 'urgentPulse 1.5s ease-in-out infinite' : 'none',
      whiteSpace: 'nowrap'
    }}>
      ⏱ {label}
    </span>
  );
}

// Full bar for picks page
export function CountdownBar({ lockAt, isLocked }) {
  const remaining = useCountdown(lockAt, isLocked);
  if (!remaining) return null;

  const urgent = remaining.diff < 3600000;
  const warn = remaining.diff < 6 * 3600000;
  const color = urgent ? '#D50A0A' : warn ? '#ffa000' : '#4caf50';
  const bg = urgent ? 'rgba(213,10,10,0.12)' : warn ? 'rgba(255,160,0,0.1)' : 'rgba(76,175,80,0.1)';
  const border = urgent ? 'rgba(213,10,10,0.3)' : warn ? 'rgba(255,160,0,0.3)' : 'rgba(76,175,80,0.25)';

  const parts = [];
  if (remaining.d > 0) parts.push(remaining.d + 'd');
  parts.push(String(remaining.h).padStart(2,'0') + 'h');
  parts.push(String(remaining.m).padStart(2,'0') + 'm');
  parts.push(String(remaining.s).padStart(2,'0') + 's');

  return (
    <div style={{ background: bg, border: '1px solid ' + border, borderRadius: 8, padding: '8px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16, animation: urgent ? 'urgentPulse 1s ease-in-out infinite' : 'none' }}>⏱</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: color, letterSpacing: 0.8 }}>
          {urgent ? 'PICKS CLOSING SOON' : 'PICKS CLOSE IN'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {parts.map((p, i) => (
          <div key={i} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 6, padding: '4px 10px', textAlign: 'center', minWidth: 44 }}>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, color: color, lineHeight: 1 }}>{p}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
